<script setup>
import { onMounted, ref } from "vue";
import axios from "../axios";
import store from "../store";

const notifications = ref([]);
const isLoading = ref(true);


function timeAgo(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return `${seconds} sec ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
}


const getNotifications = async () => {
    axios.post("api/getNotifications").then((response) => {
        notifications.value = response.data;
        isLoading.value = false;
    }).catch((error) => {
        isLoading.value = false;
        console.error("Error fetching notifications:", error);
    });
};

const quoteDeleteId = ref(null);
const showProceedModel = ref(false);
const deleteQuote = (id) => {
    quoteDeleteId.value = id;
    showProceedModel.value = true;
}

const deleteNotification = async () => {
    axios.post("api/deleteNotification", { id: quoteDeleteId.value }).then((response) => {
        notifications.value = notifications.value.filter(notification => notification.id !== quoteDeleteId.value);
        store.commit('setSuccessNotification', "Notification Deleted successfully");
        store.commit('setErrorNotification', null);
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 3000);
        showProceedModel.value = false;
        console.log('Delete success:', response.data);
    }).catch((error) => {
        showProceedModel.value = false;
        console.error('Delete error:', error.response?.data || error.message);
        store.commit('setErrorNotification', error.response?.data?.error || 'Failed to delete Notification.');
        store.commit('setSuccessNotification', null);
        setTimeout(() => {
            store.commit('clearNotifications');
        }, 3000);
    });
};


const markAsRead = (id) => {
    axios.post("api/markNotificationAsRead", { id: id }).then(() => {
        getNotifications();
    }).catch(err => console.error(err));
};

const encode = (id) => {
    return encodeURIComponent(btoa(id.toString()));
};

onMounted(() => {
    getNotifications();
});

</script>

<template>
    <div class="py-4 container-fluid overflow-hidden ctm-mobile-padding">
        <div class="row py-4 mt-5">
            <div class="col-md-12">
                <div class="card p-3 p-md-5 shadow-lg rounded-xl bg-light-color text-gray-800 position-relative">
                    <div class="ctm-loader" v-if="isLoading">
                        <div class="loader" id="loader-5">
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                <div class="card">
                    <div class="card-header pb-0">
                        <div class="d-flex align-items-center">
                            <h6 class="mb-0">Notification</h6>
                        </div>
                    </div>
                    <div class="card-body" v-if="notifications.length > 0">
                        <div class="card mb-3 " :class="notification.read_at ? 'notification-card-read' : 'notification-card-unread'
                            " v-for="(notification, index) in notifications" :key="index">
                            <div class="d-flex items-center p-3 justify-content-between align-items-center">
                                <router-link :to="`/single-quotation/${encode(notification.data.q_id)}`" class="ps-3"
                                    @click="markAsRead(notification.id)">
                                    <p class="text-sm mb-1">
                                        <span class="font-semibold">{{ notification.data.message }} </span>
                                    </p>
                                    <p class="text-xs text-gray-500 mb-0"><i class="fa fa-clock me-1"></i>{{
                                        timeAgo(notification.created_at) }}</p>
                                </router-link>
                                <div>
                                    <button class="delete-notification" @click="deleteQuote(notification.id)"><svg
                                            xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                            fill="currentColor" class="bi bi-trash3" viewBox="0 0 16 16">
                                            <path
                                                d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5" />
                                        </svg></button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="card-body" v-else>
                        <p class="text-center">No notifications available.</p>
                    </div>
                </div>
                </div>
            </div>
        </div>
    </div>
    <div v-if="showProceedModel" class="modal-overlay">
        <div class="modal-container">
            <div class="modal-header">
                <h5 class="modal-title">Confirmation</h5>
                <button class="close-button" @click="showProceedModel = false">&times;</button>
            </div>
            <div class="modal-body">
                <p class="mb-0">Are you sure you want to delete a notification.?</p>
            </div>
            <div class="modal-footer">
                <button class="btn btn-bg-yellow" @click="showProceedModel = false">No</button>
                <button class="btn btn-bg" @click="deleteNotification">Yes</button>
            </div>
        </div>
    </div>
</template>
<style>
.notification-card-unread {
    border-radius: 10px !important;
    background-color: #f2e5d7 !important;
    overflow: hidden;
}

.notification-card-read {
    border-radius: 10px !important;
    background-color: #fffaf4 !important;
    overflow: hidden;
}

.delete-notification {
    border-radius: 5px;
    border: none;
    background: #a35c2e;
    color: #fff;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1050;
}

.modal-container {
    background: white;
    padding: 20px;
    border-radius: 8px;
    width: 400px;
    max-width: 90%;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.modal-body {
    margin-top: 10px;
}

.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    border-top: none !important;
}

.close-button {
    background: transparent;
    border: none;
    font-size: 24px;
    cursor: pointer;
}
</style>
